<script setup lang="ts">
/**
 * RecallAlertBanner — Persistent top banner driven by pending recalls.
 *
 * Displays the first non-session-dismissed pending recall by name, plus a
 * count badge showing how many pending recalls remain visible. Only one recall
 * is shown at a time; the banner cycles to the next when the current one is
 * session-dismissed.
 *
 * Acceptance criteria:
 *  - Appears as a top alert banner below the header
 *  - Shows the first non-session-dismissed pending recall (name + count)
 *  - Visually distinguishable as high priority (red border/background)
 *  - Clicking "View Recall" opens the same read-only ViewRecallModal dialog
 *    used by the notification bell's pending-recall list (with Approve/Reject CTAs)
 *  - Visible irrespective of which module/page the user is on
 *  - Banner hides for a specific recall only when:
 *      1. The recall is approved or rejected (removed from pendingRecalls), OR
 *      2. User explicitly closes it via the X button
 *         (session-only; reappears on next login for still-pending recalls)
 *  - Acknowledging the critical alert notification does NOT dismiss the banner
 *
 * Mounted in AppLayout.vue — always visible when any pending recall exists.
 */
import { computed, ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import { Icon, useToaster } from '@tms-phoenix/ui-vue';
import { useNotificationStore, type PendingRecallItem } from '@/stores';
import { config } from '@/core/config';
import ViewRecallModal from '@/modules/recall/components/ViewRecallModal.vue';
import RejectRecallModal from '@/modules/recall/components/RejectRecallModal.vue';
import { updateHospitalRecallStatus } from '@/modules/recall/services/recall.api';
import type { Recall } from '@/modules/recall/types';

const { t } = useTranslation();
const notificationStore = useNotificationStore();
const toast = useToaster();

/**
 * The first non-session-dismissed pending recall.
 * Sourced from the store getter so it stays in sync with AppHeader and AppLayout —
 * all three consumers derive visibility from the same reactive signal.
 */
const latestRecallAlert = computed(() => notificationStore.latestVisibleRecallAlert);

/** Count of pending, non-session-dismissed recalls. */
const recallCount = computed(() => notificationStore.visibleRecallCount);

const showRecallModal = ref(false);
const showRejectModal = ref(false);
const selectedRecall = ref<Recall | null>(null);

/** Map a PendingRecallItem from the store to the Recall view-model for ViewRecallModal. */
function toRecall(item: PendingRecallItem): Recall {
  return {
    id: item.id,
    recallNumber: item.recallNumber,
    name: item.name,
    productName: item.productName,
    recallDate: item.recallDate,
    status: 'Pending',
    manufacturer: item.manufacturer,
    affectedLotNumbers: item.affectedLotNumbers,
    linkedProductId: item.linkedProductId,
    clinicalDescription: item.clinicalDescription,
    informationSource: item.informationSource,
    sourceUrl: item.sourceUrl,
  };
}

function handleNavigateToRecall() {
  if (!latestRecallAlert.value) return;
  // Open the dialog only — do NOT remove from pending list.
  // The banner stays until the recall is approved, rejected, or explicitly closed.
  selectedRecall.value = toRecall(latestRecallAlert.value);
  showRecallModal.value = true;
}

function handleDismiss() {
  if (!latestRecallAlert.value) return;
  // Session-dismiss by recall ID. Banner reappears after logout/login
  // if the recall is still pending.
  notificationStore.dismissRecallNotification(latestRecallAlert.value.id);
}

async function handleApprove(recall: Recall) {
  const hospitalId = config.hospitalId;
  const staffId = config.staffId;
  if (!hospitalId) return;

  try {
    await updateHospitalRecallStatus(recall.id, {
      hospital_id: hospitalId,
      status: 'APPROVED',
      acknowledged_by: staffId || undefined,
    });
    notificationStore.removePendingRecall(recall.id);
    showRecallModal.value = false;
    selectedRecall.value = null;
    toast.success(t('recall.bell.approveSuccess', 'Recall approved successfully.'));
  } catch (err) {
    console.error('[RecallAlertBanner] Failed to approve recall:', err);
    toast.danger(t('recall.bell.approveError', 'Failed to approve recall. Please try again.'));
  }
}

function handleRejectOpen(recall: Recall) {
  selectedRecall.value = recall;
  showRecallModal.value = false;
  showRejectModal.value = true;
}

async function handleRejectConfirm(recall: Recall, reason: string) {
  const hospitalId = config.hospitalId;
  const staffId = config.staffId;
  if (!hospitalId) return;

  try {
    await updateHospitalRecallStatus(recall.id, {
      hospital_id: hospitalId,
      status: 'REJECTED',
      reason,
      acknowledged_by: staffId || undefined,
    });
    notificationStore.removePendingRecall(recall.id);
    showRejectModal.value = false;
    selectedRecall.value = null;
    toast.success(t('recall.bell.rejectSuccess', 'Recall rejected successfully.'));
  } catch (err) {
    console.error('[RecallAlertBanner] Failed to reject recall:', err);
    toast.danger(t('recall.bell.rejectError', 'Failed to reject recall. Please try again.'));
  }
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-1"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-1"
  >
    <div
      v-if="latestRecallAlert"
      class="shrink-0 mx-4 rounded-md bg-red-50 border border-red-200 shadow-sm"
      role="alert"
      aria-live="assertive"
    >
      <div class="px-4 py-2 flex items-center justify-between gap-3">
        <!-- Left: icon + count + severity badge -->
        <div class="flex items-center gap-2 min-w-0">
          <!-- Warning triangle -->
          <Icon
            name="IconWarning"
            size-class="h-4 w-4 shrink-0"
            variant="stroke"
            color="text-red-500"
          />

          <!-- Count label -->
          <span class="text-sm font-semibold text-gray-900 shrink-0">
            {{ recallCount }} {{ recallCount === 1 ? t('notifications.banner.activeRecallSingular', 'Active Recall') : t('notifications.banner.activeRecallPlural', 'Active Recalls') }}
          </span>

          <!-- Severity / subject pill -->
          <span class="inline-flex items-center rounded-full bg-red-100 border border-red-300 px-2.5 py-0.5 text-xs font-semibold text-red-700 truncate max-w-xs">
            {{ latestRecallAlert.name }}
          </span>
        </div>

        <!-- Right: view link + dismiss -->
        <div class="flex items-center gap-1 shrink-0">
          <Button
            text
            plain
            class="text-sm! font-medium! text-red-600! hover:text-red-800! p-0! border-none! bg-transparent! gap-0.5!"
            :aria-label="t('recall.action.viewRecall')"
            @click="handleNavigateToRecall"
          >
            {{ t('recall.action.viewRecall') }}
            <Icon
              name="AngleRight"
              size-class="h-3.5 w-3.5 ml-0.5"
              color="text-red-600"
            />
          </Button>

          <span class="mx-1 text-red-200 select-none">|</span>

          <!-- Dismiss -->
          <Button
            text
            plain
            class="p-0! border-none! bg-transparent! text-gray-400! hover:text-gray-600! flex! items-center! justify-center!"
            :aria-label="t('notifications.banner.dismissAriaLabel')"
            @click.stop="handleDismiss"
          >
            <Icon
              name="Close"
              size-class="h-3.5 w-3.5"
              color="text-gray-600"
            />
          </Button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Recall Detail Modal (same dialog as the notification bell's pending-recall list) -->
  <ViewRecallModal
    :visible="showRecallModal"
    :recall="selectedRecall"
    @update:visible="showRecallModal = $event"
    @approve="handleApprove"
    @reject="handleRejectOpen"
  />

  <!-- Reject Reason Modal -->
  <RejectRecallModal
    :visible="showRejectModal"
    :recall="selectedRecall"
    @update:visible="showRejectModal = $event"
    @confirm="handleRejectConfirm"
  />
</template>
